import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  FormFields,
  PermissionButton,
  getDefaultValuesForForm,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import schema from './create.json';
import validationSchema from './validations';
import PasswordInstructions from './PasswordInstructions';
import { FORM_VIEW } from '../../../constants';

const FORM_ID = 'ServiceProviderUserCreateForm';

function UserCreate({ serviceProviderId: pageId, currentIndex }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const postApiUrl = `${getApiUrl()}/sxcl/v1/users`;
  const yupValidationSchema = validationSchema(formatMessage, getApiUrl, get);

  const [insightModulePurchased, setInsightModulePurchased] = useState(false);

  const getEditUrl = newId => {
    return `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
      FORM_VIEW.EDIT
    }/${newId}`;
  };

  const getServiceModule = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-modules`;
    const response = await get(url);
    return response;
  };

  const preProcessRequestData = data => {
    const { enabled, insightPurchased, role, ...other } = data;
    return {
      enabled: enabled === 'true',
      companyId: pageId,
      userRoleId: role.value,
      ...other,
    };
  };

  const onSubmit = async data => {
    const requestData = preProcessRequestData(data);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_CREATE_SUCCESS',
          defaultMessage: 'User was saved successfully',
        }),
      );
      return {
        nextValues: {
          ...data,
          insightPurchased: insightModulePurchased,
        },
        errors: null,
        callback: () => navigate(getEditUrl(response?.data?.id)),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const responseServiceModule = await getServiceModule();

    if (responseServiceModule?.ok) {
      const result = {
        data: {
          ...getDefaultValuesForForm({
            schema,
          }),
          smsOptedIn: false,
          insightPurchased:
            responseServiceModule?.data?.insightPurchased || false,
        },
        errors: null,
      };
      setInsightModulePurchased(
        responseServiceModule?.data?.insightPurchased || false,
      );
      return result;
    }

    return {
      data: null,
      errors: responseServiceModule?.errors,
    };
  }, []);

  return (
    <Box>
      <Form
        formId={FORM_ID}
        ref={formRef}
        permittedByDefault
        onSubmit={onSubmit}
        schema={schema}
        customComponents={{ ...FormFields, PasswordInstructions }}
        fetchDefaultValues={fetchDefaultValues}
        validationSchema={yupValidationSchema}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            permissionName="CAN_CREATE_SP_USER"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'USER_CREATE_SAVE_BUTTON',
              defaultMessage: 'Save User',
            })}
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_CREATE_SP_USER"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => {
              formRef.current?.reset();
            }}>
            {formatMessage({
              id: 'USER_CREATE_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </Box>
  );
}

export default observer(UserCreate);
